import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';

// ── PKCE / Authentik wiring ─────────────────────────────────────────────────
// The stock @backstage/plugin-api-docs OpenApiDefinitionWidget cannot enable
// PKCE: it exposes only `definition`/`requestInterceptor`/`supportedSubmitMethods`
// and never calls SwaggerUI's `initOAuth`. So we render SwaggerUI ourselves and
// supply the runtime-only bits (clientId + the PKCE toggle) via `onComplete`.
//
// The OpenAPI spec (served by each service, e.g. iot-api-test) declares the
// `oauth2` authorizationCode flow + URLs + scopes; this only adds what the spec
// cannot carry. `clientId` is the shared public Authentik provider; it is not a
// secret (PKCE replaces the client secret for public clients).
const PKCE_CLIENT_ID = 'toolsense-api-interactive';
const PKCE_SCOPES = ['openid', 'profile', 'toolsense-api-svc'];

// Theming copied from the plugin's (unexported) OpenApiDefinition so the
// embedded SwaggerUI matches the Backstage MUI theme (incl. dark mode).
const useStyles = makeStyles(theme => ({
  root: {
    '& .swagger-ui': {
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      ['& .btn-clear']: { color: theme.palette.text.primary },
      ['& .scheme-container']: {
        backgroundColor: theme.palette.background.default,
      },
      [`& .opblock-tag,
          .opblock-tag small,
          table thead tr td,
          table thead tr th,
          table tbody tr td,
          table tbody tr th`]: {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      [`& section.models,
          section.models.is-open h4`]: {
        borderColor: theme.palette.divider,
      },
      [`& .model-title,
          .model .renderedMarkdown,
          .model .description`]: {
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
      },
      [`& h1, h2, h3, h4, h5, h6,
          .errors h4, .error h4, .opblock h4, section.models h4,
          .response-control-media-type__accept-message,
          .opblock-summary-description,
          .opblock-summary-operation-id,
          .opblock-summary-path,
          .opblock-summary-path__deprecated,
          .opblock-description-wrapper,
          .opblock-external-docs-wrapper,
          .opblock-section-header .btn,
          .opblock-section-header>label,
          .scheme-container .schemes>label,a.nostyle,
          .parameter__name,
          .response-col_status,
          .response-col_links,
          .error .btn,
          .info .title,
          .info .base-url`]: {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
      },
      [`& .opblock .opblock-section-header,
          .model-box,
          section.models .model-container`]: {
        background: theme.palette.background.default,
      },
      [`& .prop-format,
          .parameter__in`]: {
        color: theme.palette.text.disabled,
      },
      [`& table.model,
          .parameter__type,
          .model.model-title,
          .model-title,
          .model span,
          .model .brace-open,
          .model .brace-close,
          .model .property.primitive,
          .model .renderedMarkdown,
          .model .description,
          .errors small`]: {
        color: theme.palette.text.secondary,
      },
      [`& .parameter__name.required:after,
        .parameter__name.required span`]: {
        color: theme.palette.warning.dark,
      },
      [`& table.model,
          table.model .model,
          .opblock-external-docs-wrapper`]: {
        fontSize: theme.typography.fontSize,
      },
      [`& table.headers td`]: {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
      },
      [`& .model-hint`]: {
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
      },
      [`& .opblock-summary-method,
          .info a`]: {
        fontFamily: theme.typography.fontFamily,
      },
      [`& .info, .opblock, .tab`]: {
        [`& li, p`]: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        },
      },
      [`& a`]: { color: theme.palette.primary.main },
      [`& .renderedMarkdown code`]: { color: theme.palette.secondary.light },
      [`& .property-row td:first-child`]: { color: theme.palette.text.primary },
      [`& span.prop-type`]: { color: theme.palette.success.light },
      [`& .opblock-control-arrow svg, .authorization__btn .unlocked`]: {
        fill: theme.palette.text.primary,
      },
      [`& input[type=text],
          & input[type=password],
          & input[type=email],
          & input[type=number],
          & textarea,
          & select`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      // Hide the client_secret field in the Authorize dialog. This is a PUBLIC
      // PKCE client (no secret); swagger-ui always renders the field for the
      // authorizationCode flow, and typing a value would actually break the
      // token exchange. swagger-ui gives the input a stable id
      // `client_secret_<flow>`, so drop its whole `.wrapper` row via :has().
      [`& .auth-container .wrapper:has(input[id^="client_secret_"])`]: {
        display: 'none',
      },
      // Fallback for browsers without :has() — hide the label + input directly.
      [`& .auth-container label[for^="client_secret_"],
          & .auth-container input[id^="client_secret_"]`]: {
        display: 'none',
      },
      // client_id is fixed (the public PKCE provider) and made read-only via JS;
      // grey it so it reads as non-editable.
      [`& .auth-container input[id^="client_id_"][readonly]`]: {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
  },
}));

/**
 * SwaggerUI renderer for `openapi` API entities that enables the Authorization
 * Code + PKCE login against Authentik. Drop-in replacement for the stock
 * OpenApiDefinitionWidget; wired via the apiDocsConfigRef override.
 */
export const OpenApiPkceDefinition = ({
  definition,
}: {
  definition: string;
}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  // Same deferred-set trick the plugin uses so SwaggerUI mounts cleanly.
  const [def, setDef] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDef(definition), 0);
    return () => clearTimeout(timer);
  }, [definition]);

  // The Authorize dialog mounts lazily (on click), so set `readonly` on the
  // client_id input whenever it appears. clientId is fixed by initOAuth; this
  // stops it being edited into a broken value. Scoped to this widget's subtree.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;
    const markReadOnly = () => {
      root
        .querySelectorAll<HTMLInputElement>('input[id^="client_id_"]')
        .forEach(input => {
          input.readOnly = true;
        });
    };
    const observer = new MutationObserver(markReadOnly);
    observer.observe(root, { childList: true, subtree: true });
    markReadOnly();
    return () => observer.disconnect();
  }, []);

  return (
    <div className={classes.root} ref={containerRef}>
      <SwaggerUI
        spec={def}
        url=""
        deepLinking
        oauth2RedirectUrl={`${window.location.protocol}//${window.location.host}/oauth2-redirect.html`}
        onComplete={(system: any) =>
          system.initOAuth({
            clientId: PKCE_CLIENT_ID,
            usePkceWithAuthorizationCodeGrant: true,
            scopes: PKCE_SCOPES,
          })
        }
      />
    </div>
  );
};
